import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import config from '../../data/pdfLibraryConfig.json';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { SearchIcon } from '../ui/Icons';
import {
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  BuildingLibraryIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function PdfLibrary({
  fixedCategory = null,
  hideEmptySearch = false,
  searchPlaceholder = "Rechercher par nom, catégorie, ou faculté..."
}) {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(fixedCategory || 'tous');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPdfPreview, setSelectedPdfPreview] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const itemsPerPage = 20;

  const fetchPdfs = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    const cacheKey = `acem-pdf-library-v2-${config.github.repoOwner}-${config.github.repoName}`;

    if (!forceRefresh) {
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}-time`);

      const ONE_HOUR = 60 * 60 * 1000;
      if (cachedData && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < ONE_HOUR) {
        setPdfs(JSON.parse(cachedData));
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(
        `https://api.github.com/repos/${config.github.repoOwner}/${config.github.repoName}/git/trees/${config.github.branch}?recursive=1`
      );

      if (!response.ok) {
        throw new Error("Impossible de récupérer les documents.");
      }

      const data = await response.json();

      const pdfFiles = data.tree
        .filter(
          item =>
            item.type === 'blob' &&
            item.path.startsWith(config.github.basePath) &&
            item.path.toLowerCase().endsWith('.pdf')
        )
        .map(item => {
          const pathParts = item.path.split('/');
          const fileNameWithExt = pathParts[pathParts.length - 1];
          const name = fileNameWithExt.substring(0, fileNameWithExt.lastIndexOf('.'));

          let categoryName = "Autre";
          let faculty = null;
          let cycle = null;
          let filiere = null;
          let annee = null;
          let semestre = null;
          let extraTags = [];

          const relativeParts = item.path.substring(config.github.basePath.length + 1).split('/');

          if (relativeParts.length >= 2) {
            categoryName = relativeParts[0];
          }

          const folders = relativeParts.slice(1, -1);

          folders.forEach((folder, index) => {
            const lowerFolder = folder.toLowerCase();
            if (/^s[1-6]$/i.test(lowerFolder) || lowerFolder.includes('semestre')) {
              semestre = folder;
            } else if (lowerFolder.includes('année') || lowerFolder.includes('annee')) {
              annee = folder;
            } else if (index === 0) {
              faculty = folder;
            } else if (index === 1) {
              cycle = folder;
            } else if (index === 2) {
              filiere = folder;
            } else {
              extraTags.push(folder);
            }
          });

          const catConfig =
            config.categories.find(c => c.folderName === categoryName) ||
            config.categories.find(c => c.folderName === null);

          const sizeKb = item.size / 1024;
          const sizeFormatted =
            sizeKb > 1024
              ? `${(sizeKb / 1024).toFixed(2)} Mo`
              : `${sizeKb.toFixed(0)} Ko`;

          const downloadUrl = `https://raw.githubusercontent.com/${config.github.repoOwner}/${config.github.repoName}/${config.github.branch}/${item.path
            .split('/')
            .map(encodeURIComponent)
            .join('/')}`;
          const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl)}&embedded=true`;

          return {
            id: item.path,
            name,
            category: categoryName,
            categoryColor: catConfig ? catConfig.color : '#1a6b3a',
            faculty,
            cycle,
            filiere,
            annee,
            semestre,
            extraTags,
            size: sizeFormatted,
            downloadUrl,
            previewUrl
          };
        });

      setPdfs(pdfFiles);
      sessionStorage.setItem(cacheKey, JSON.stringify(pdfFiles));
      sessionStorage.setItem(`${cacheKey}-time`, Date.now().toString());
    } catch (err) {
      console.error("Error fetching PDFs:", err);
      setError("Impossible de récupérer les documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdfs();
  }, []);

  const filteredPdfs = useMemo(() => {
    let result = pdfs;

    if (selectedCategory !== 'tous') {
      const catConfig = config.categories.find(c => c.id === selectedCategory);
      if (catConfig && catConfig.folderName) {
        result = result.filter(
          pdf => pdf.category.trim().toLowerCase() === catConfig.folderName.trim().toLowerCase()
        );
      }
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        pdf =>
          pdf.name.toLowerCase().includes(term) ||
          pdf.category.toLowerCase().includes(term) ||
          (pdf.faculty && pdf.faculty.toLowerCase().includes(term)) ||
          (pdf.cycle && pdf.cycle.toLowerCase().includes(term)) ||
          (pdf.filiere && pdf.filiere.toLowerCase().includes(term)) ||
          (pdf.annee && pdf.annee.toLowerCase().includes(term)) ||
          (pdf.semestre && pdf.semestre.toLowerCase().includes(term)) ||
          (pdf.extraTags && pdf.extraTags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    return result;
  }, [pdfs, searchTerm, selectedCategory]);

  const totalPages = Math.ceil(filteredPdfs.length / itemsPerPage);
  const paginatedPdfs = filteredPdfs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handlePreview = async (pdf) => {
    setSelectedPdfPreview(pdf);
    setPreviewLoading(true);
    setPreviewError(null);
    document.body.style.overflow = 'hidden';

    try {
      const response = await fetch(pdf.downloadUrl);
      if (!response.ok) {
        throw new Error(`Erreur serveur (${response.status})`);
      }
      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPreviewBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return blobUrl;
      });
    } catch (err) {
      console.error("Erreur lors du chargement de l'aperçu PDF:", err);
      setPreviewError("Impossible de charger l'aperçu dynamique du document. Vous pouvez tout de même le télécharger directement.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setSelectedPdfPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  return (
    <div className="w-full space-y-6">
      {!fixedCategory && (
        <Card variant="default" className="p-6 text-center space-y-2 max-w-4xl mx-auto">
          <p className="text-acem-text text-sm sm:text-base leading-relaxed">
            Bienvenue dans la <strong>bibliothèque numérique de l'ACEM Fès</strong>. Vous y trouverez nos documents officiels, les cours, TD et sujets d'examens des différentes facultés, ainsi qu'une sélection de livres et de ressources académiques.
          </p>
          <p className="text-xs text-acem-muted italic">
            <strong>Note :</strong> Cette bibliothèque est continuellement enrichie de nouvelles ressources par la commission pédagogique.
          </p>
        </Card>
      )}

      {/* Barre de contrôle et recherche */}
      <div className="space-y-4 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <div className="relative flex-1 w-full">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-acem-muted" />
            <input
              type="text"
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-acem-border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 focus:ring-acem-primary/30 focus:border-acem-primary transition-all shadow-sm"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            onClick={() => fetchPdfs(true)}
            variant="secondary"
            size="md"
            className="w-full sm:w-auto"
            title="Forcer la mise à jour depuis GitHub"
          >
            <ArrowPathIcon className="w-4 h-4 mr-1.5" aria-hidden="true" />
            Actualiser
          </Button>
        </div>

        {/* Filtres de catégories */}
        {!fixedCategory && (
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {config.categories.map((cat) => {
              const isActive = selectedCategory === cat.id;
              const catBgColor = cat.color || '#1a6b3a';
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  style={isActive ? { backgroundColor: catBgColor, color: '#ffffff' } : {}}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-acem-primary/40 ${isActive
                    ? "text-white shadow-sm scale-105"
                    : "bg-white text-acem-text hover:text-acem-primary border border-acem-border hover:border-acem-primary/50 hover:bg-acem-surface-container-low"
                    }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* État de chargement / erreur / liste */}
      {loading ? (
        <div className="text-center py-12 text-acem-muted font-medium animate-pulse">
          Chargement des documents en cours...
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-600 bg-red-50 rounded-xl max-w-xl mx-auto border border-red-200 text-sm">
          {error}
        </div>
      ) : hideEmptySearch && !searchTerm.trim() ? (
        <div className="text-center py-8 text-acem-muted italic text-sm">
          Commencez à taper un mot-clé pour afficher les résultats...
        </div>
      ) : filteredPdfs.length === 0 ? (
        <div className="text-center py-12 text-acem-muted text-sm bg-white rounded-xl border border-acem-border/60 max-w-xl mx-auto">
          Aucun document ne correspond à votre recherche.
        </div>
      ) : (
        <div className="space-y-6 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginatedPdfs.map((pdf) => (
              <Card
                key={pdf.id}
                variant="elevated"
                className="p-4 flex flex-col justify-between gap-3 group relative overflow-hidden"
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2.5">
                      <DocumentTextIcon className="w-6 h-6 text-acem-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <div>
                        <h4 className="font-bold text-sm sm:text-base text-acem-text leading-snug line-clamp-2" title={pdf.name}>
                          {pdf.name}
                        </h4>
                        <span className="text-xs text-acem-muted font-medium">{pdf.size}</span>
                      </div>
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white flex-shrink-0"
                      style={{ backgroundColor: pdf.categoryColor }}
                    >
                      {pdf.category}
                    </span>
                  </div>

                  {/* Metadata tags */}
                  <div className="flex flex-wrap gap-1.5 text-xs text-acem-muted pt-1">
                    {pdf.faculty && (
                      <span className="inline-flex items-center gap-1 bg-acem-surface-container-low px-2 py-0.5 rounded-md">
                        <BuildingLibraryIcon className="w-3.5 h-3.5 text-acem-primary" aria-hidden="true" /> {pdf.faculty}
                      </span>
                    )}
                    {pdf.cycle && (
                      <span className="inline-flex items-center gap-1 bg-acem-surface-container-low px-2 py-0.5 rounded-md">
                        <AcademicCapIcon className="w-3.5 h-3.5 text-acem-primary" aria-hidden="true" /> {pdf.cycle}
                      </span>
                    )}
                    {pdf.filiere && (
                      <span className="inline-flex items-center gap-1 bg-acem-surface-container-low px-2 py-0.5 rounded-md">
                        <BookOpenIcon className="w-3.5 h-3.5 text-acem-primary" aria-hidden="true" /> {pdf.filiere}
                      </span>
                    )}
                    {pdf.annee && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md font-bold">
                        {pdf.annee}
                      </span>
                    )}
                    {pdf.semestre && (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md font-bold">
                        {pdf.semestre}
                      </span>
                    )}
                    {pdf.extraTags?.map((tag, idx) => (
                      <span key={idx} className="bg-acem-surface-container-high px-2 py-0.5 rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-acem-border/50">
                  <button
                    type="button"
                    onClick={() => handlePreview(pdf)}
                    className="flex-1 py-1.5 px-3 rounded-lg border border-acem-primary text-acem-primary hover:bg-acem-primary hover:text-white transition-all text-xs font-bold flex items-center justify-center gap-1.5"
                  >
                    <EyeIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    Aperçu
                  </button>
                  <a
                    href={pdf.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex-1 py-1.5 px-3 rounded-lg bg-acem-primary text-white hover:bg-acem-primary-dark transition-all text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    Télécharger
                  </a>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-6">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Précédent
              </Button>
              <span className="text-xs font-bold text-acem-muted">
                Page {currentPage} sur {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Suivant
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modal Aperçu PDF Natif (via Portal sur document.body) */}
      {selectedPdfPreview && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden border border-acem-border animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-acem-border bg-acem-surface-container">
              <div className="flex items-center gap-2.5 min-w-0 max-w-[60%] sm:max-w-[70%]">
                <DocumentTextIcon className="w-5 h-5 text-acem-primary flex-shrink-0" aria-hidden="true" />
                <h3 className="font-bold text-sm sm:text-base text-acem-primary-dark truncate" title={selectedPdfPreview.name}>
                  {selectedPdfPreview.name}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {previewBlobUrl && (
                  <Button
                    href={previewBlobUrl}
                    variant="secondary"
                    size="sm"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:inline-flex"
                    title="Ouvrir en plein écran dans un nouvel onglet"
                  >
                    Plein écran
                  </Button>
                )}
                <Button
                  href={selectedPdfPreview.downloadUrl}
                  variant="primary"
                  size="sm"
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5 mr-1" />
                  Télécharger
                </Button>
                <button
                  type="button"
                  onClick={closePreview}
                  className="text-acem-muted hover:text-acem-text text-2xl font-bold p-1 leading-none rounded-lg hover:bg-black/5 transition-colors"
                  aria-label="Fermer"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="flex-1 bg-gray-100 relative overflow-hidden flex items-center justify-center">
              {previewLoading && (
                <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
                  <div className="w-10 h-10 border-4 border-acem-primary/20 border-t-acem-primary rounded-full animate-spin"></div>
                  <p className="font-bold text-sm text-acem-text">Chargement du document PDF...</p>
                  <p className="text-xs text-acem-muted">Taille : {selectedPdfPreview.size}</p>
                </div>
              )}

              {previewError && !previewLoading && (
                <div className="flex flex-col items-center justify-center gap-4 p-6 text-center max-w-md">
                  <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">
                    !
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-base text-acem-text">Aperçu indisponible</h4>
                    <p className="text-xs text-acem-muted leading-relaxed">{previewError}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePreview(selectedPdfPreview)}
                    >
                      Réessayer
                    </Button>
                    <Button
                      href={selectedPdfPreview.downloadUrl}
                      variant="primary"
                      size="sm"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Télécharger le fichier
                    </Button>
                  </div>
                </div>
              )}

              {!previewLoading && !previewError && previewBlobUrl && (
                <iframe
                  src={previewBlobUrl}
                  className="w-full h-full border-0"
                  title={`Aperçu de ${selectedPdfPreview.name}`}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
